export interface ReleaseManifest {
  version: string;
  uiRelease?: string;
  publishedAt?: string;
  entry?: string;
}

export const RELEASE_MANIFEST_PATH = '/release.json';
export const DASHBOARD_RELEASE_MANIFEST_PATH = '/dashboard-release.json';
export const RELEASE_RELOAD_TARGET_KEY = 'tc_release_reload_target';
export const REMOVE_CHILD_RECOVERY_TARGET_KEY = 'tc_remove_child_recovery_target';
export const RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const REMOVE_CHILD_ERROR_MESSAGE =
  "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.";

const SENSITIVE_ROUTE_PREFIXES = [
  '/admin',
  '/billing',
  '/checkout',
  '/dashboard',
  '/login',
  '/payment',
  '/profile',
  '/projects',
  '/register',
  '/reset-password',
  '/settings',
  '/signup',
  '/wallet',
];

export const normalizeReleaseVersion = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const isReleaseManifest = (value: unknown): value is ReleaseManifest => {
  if (!value || typeof value !== 'object') return false;
  return normalizeReleaseVersion((value as ReleaseManifest).version).length > 0;
};

export const hasReleaseChanged = (
  currentVersion: unknown,
  manifest: unknown,
): manifest is ReleaseManifest => {
  if (!isReleaseManifest(manifest)) return false;
  const current = normalizeReleaseVersion(currentVersion);
  return current.length > 0 && current !== normalizeReleaseVersion(manifest.version);
};

export const isSensitiveReleaseRoute = (pathname: string): boolean => {
  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return SENSITIVE_ROUTE_PREFIXES.some(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  );
};

export const getReleaseManifestPath = (pathname: string): string =>
  isSensitiveReleaseRoute(pathname)
    ? DASHBOARD_RELEASE_MANIFEST_PATH
    : RELEASE_MANIFEST_PATH;

export const isRemoveChildFailurePage = (textContent: unknown): boolean =>
  typeof textContent === 'string' && textContent.includes(REMOVE_CHILD_ERROR_MESSAGE);

export const buildReleaseReloadUrl = (
  href: string,
  version: string,
  cacheBust: number = Date.now(),
): string => {
  const url = new URL(href);
  url.searchParams.set('tc_release', normalizeReleaseVersion(version) || cacheBust.toString());
  url.searchParams.set('tc_reload', cacheBust.toString());
  return url.toString();
};

const getReloadTarget = (): string | null => {
  try {
    return window.sessionStorage.getItem(RELEASE_RELOAD_TARGET_KEY);
  } catch {
    return null;
  }
};

const setReloadTarget = (version: string): void => {
  try {
    window.sessionStorage.setItem(RELEASE_RELOAD_TARGET_KEY, version);
  } catch {
    // Storage can be blocked by privacy settings; the update flow must still work.
  }
};

const forceReleaseReload = (version: string): void => {
  setReloadTarget(version);
  window.location.replace(buildReleaseReloadUrl(window.location.href, version));
};

const installRemoveChildRecovery = (currentVersion: string): (() => void) => {
  const recover = (): void => {
    if (!isRemoveChildFailurePage(document.body?.textContent)) return;

    try {
      if (window.sessionStorage.getItem(REMOVE_CHILD_RECOVERY_TARGET_KEY) === currentVersion) {
        // Recovery already attempted once for this version — stop rather than
        // reload-looping. Previously this prompted the user; the prompt was
        // removed, so we simply leave the page as it is.
        return;
      }
      window.sessionStorage.setItem(REMOVE_CHILD_RECOVERY_TARGET_KEY, currentVersion);
    } catch {
      // Continue with the one-time recovery when session storage is unavailable.
    }

    forceReleaseReload(currentVersion);
  };

  recover();
  const observer = new MutationObserver(recover);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
};

export interface ReleaseUpdateMonitorOptions {
  initialDelayMs?: number;
  intervalMs?: number;
  manifestPath?: string;
}

export const installReleaseUpdateMonitor = (
  options: ReleaseUpdateMonitorOptions = {},
): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => undefined;

  const currentVersion = normalizeReleaseVersion(document.documentElement.dataset.releaseVersion);
  if (!currentVersion) return () => undefined;

  const stopRemoveChildRecovery = installRemoveChildRecovery(currentVersion);

  const manifestPath =
    options.manifestPath ?? getReleaseManifestPath(window.location.pathname);
  const intervalMs = options.intervalMs ?? RELEASE_CHECK_INTERVAL_MS;
  const initialDelayMs = options.initialDelayMs ?? 5000;
  let checking = false;
  let updateFound = false;

  const check = async (): Promise<void> => {
    if (checking || updateFound || !navigator.onLine) return;
    checking = true;

    try {
      const separator = manifestPath.includes('?') ? '&' : '?';
      const response = await fetch(`${manifestPath}${separator}t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const manifest: unknown = await response.json();
      if (!hasReleaseChanged(currentVersion, manifest)) return;

      updateFound = true;
      const nextVersion = normalizeReleaseVersion(manifest.version);
      const reloadTarget = getReloadTarget();

      if (!isSensitiveReleaseRoute(window.location.pathname) && reloadTarget !== nextVersion) {
        forceReleaseReload(nextVersion);
        return;
      }

      // Sensitive route (signup, checkout, dashboard): reloading here would throw
      // away whatever the user has typed, so the new release is simply left for
      // their next navigation. No prompt — the update banner was removed.
    } catch {
      // A failed version check must never interfere with the application.
    } finally {
      checking = false;
    }
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') void check();
  };
  const onFocus = (): void => void check();
  const onOnline = (): void => void check();

  const initialTimer = window.setTimeout(() => void check(), initialDelayMs);
  const intervalTimer = window.setInterval(() => void check(), intervalMs);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onOnline);

  return () => {
    stopRemoveChildRecovery();
    window.clearTimeout(initialTimer);
    window.clearInterval(intervalTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onOnline);
  };
};

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  installReleaseUpdateMonitor();
}
